import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { clearAllAuthStorage, getAccessToken } from '@/lib/auth-storage'
import {
  clearParkingOsSession,
  getEffectiveAccessToken,
  getParkingOsSession,
  isSessionKickedMessage,
} from '@/lib/parking-session'
import { isAuthErrorCode, toFriendlyAuthExpiredMessage } from '@/lib/api-response'

type AuthAwareAxiosConfig = InternalAxiosRequestConfig & {
  skipAuthExpiredRedirect?: boolean
}

export type AuthAwareRequestConfig = Partial<InternalAxiosRequestConfig> & {
  skipAuthExpiredRedirect?: boolean
}

function dynamicProtocol(url: string): string {
  if (!url) return ''
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.host}${url.startsWith('/') ? '' : '/'}${url}`
  }
  return url
}

const kioskBaseRaw = import.meta.env.VITE_KIOSK_API_BASE || import.meta.env.VITE_API_BASE || '/kiosk'
const parkBaseRaw = import.meta.env.VITE_PARK_API_BASE || kioskBaseRaw

export const BASE_URLS = {
  adminBase: dynamicProtocol(kioskBaseRaw),
  parkBase: dynamicProtocol(parkBaseRaw),
  kioskBase: dynamicProtocol(kioskBaseRaw),
}

const timeout = Number(import.meta.env.VITE_REQUEST_TIMEOUT) || 15000

let authExpiredRedirecting = false

function redirectToLogin(message?: string): void {
  if (authExpiredRedirecting || typeof window === 'undefined') return
  authExpiredRedirecting = true
  clearAllAuthStorage()
  clearParkingOsSession()
  const params = new URLSearchParams()
  if (message) params.set('reason', message)
  window.location.replace(`/login${params.toString() ? `?${params.toString()}` : ''}`)
}

function handleAuthExpiredResponse(data: unknown, config?: AuthAwareAxiosConfig): void {
  if (config?.skipAuthExpiredRedirect) return
  const payload = (data && typeof data === 'object' ? data : {}) as { code?: number; msg?: string }
  const rawMessage = payload.msg || ''
  // 被挤下线等特殊原因保留原文展示
  if (isSessionKickedMessage(rawMessage)) {
    redirectToLogin(rawMessage)
    return
  }
  if (isAuthErrorCode(payload.code ?? null)) {
    redirectToLogin(toFriendlyAuthExpiredMessage(rawMessage) || '登录已过期，请重新登录')
  }
}

function createAxiosInstance(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout,
    headers: { 'Content-Type': 'application/json' },
  })

  instance.interceptors.request.use((config: AuthAwareAxiosConfig) => {
    const token = getEffectiveAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const config = response.config as AuthAwareAxiosConfig
      const data = response.data
      if (data && typeof data === 'object' && 'code' in data) {
        const code = (data as { code?: number }).code
        if (isAuthErrorCode(code ?? null)) {
          handleAuthExpiredResponse(data, config)
        }
      }
      return response
    },
    (error: AxiosError) => {
      const config = error.config as AuthAwareAxiosConfig | undefined
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleAuthExpiredResponse(error.response.data, config)
      }
      return Promise.reject(error)
    },
  )

  return instance
}

export const kioskHttp = createAxiosInstance(BASE_URLS.kioskBase)
export const adminHttp = kioskHttp
export const parkHttp = createAxiosInstance(BASE_URLS.parkBase)

export async function postRequest<T = unknown>(
  path: string,
  data?: unknown,
  config?: AuthAwareRequestConfig,
  client: AxiosInstance = kioskHttp,
): Promise<AxiosResponse<T>> {
  return client.post<T>(path, data, config)
}

export async function getRequest<T = unknown>(
  path: string,
  params?: Record<string, unknown>,
  config?: AuthAwareRequestConfig,
  client: AxiosInstance = kioskHttp,
): Promise<AxiosResponse<T>> {
  return client.get<T>(path, { params, ...config })
}

export function getParkStreamBaseUrl(): string {
  return BASE_URLS.parkBase.replace(/\/$/, '')
}

export function getAccessTokenOrThrow(): string {
  const token = getAccessToken() || getParkingOsSession()?.token
  if (!token) throw new Error('未登录')
  return token
}

export async function authFetch(input: RequestInfo | URL, init: RequestInit & { skipAuthExpiredRedirect?: boolean } = {}): Promise<Response> {
  const headers = new Headers(init.headers)
  if (typeof window !== 'undefined' && !headers.has('Authorization')) {
    const token = getEffectiveAccessToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
  }
  return fetch(input, { ...init, headers })
}
